// =============================================================================
// nexusApiCore — PURE, dependency-free helpers for the nexus-core REST client.
// Split out from nexusApi.ts (which imports the Supabase client) so URL/error
// logic is unit-testable without Supabase env at import time.
// =============================================================================

/** Base URL for nexus-core REST (Vercel rewrite `/nexus-api/*` → nexus-core). */
export const NEXUS_API_URL = import.meta.env.VITE_NEXUS_API_URL || '/nexus-api';

/** Error carrying the HTTP status so callers can branch (e.g. 403 = guard/RLS). */
export class NexusApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'NexusApiError';
  }
}

/** Join the nexus base with a `/api/nexus/...` path, collapsing a double slash. PURE. */
export function resolveNexusUrl(path: string, base: string = NEXUS_API_URL): string {
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

/**
 * Detect the `{ error: string }` failure envelope that some nexus-core routes
 * return WITH an HTTP 2xx status (e.g. the innovation approve/reject handlers
 * `return { error: 'Failed to ...' }` without a non-2xx code). Callers must
 * treat this as a failure, otherwise a "success" resolve would hide the error
 * at the approval choke-point. Returns the error message, or null if the body
 * is not an error envelope. PURE.
 */
export function nexusErrorEnvelope(body: unknown): string | null {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const err = (body as Record<string, unknown>).error;
    if (typeof err === 'string' && err.trim().length > 0) return err;
  }
  return null;
}

/**
 * Zdanie z koperty błędu nexus-core — `message`, potem `error` — albo surowy
 * tekst, gdy odpowiedź nie jest JSON-em. PURE.
 *
 * Trasy nexus-core odpowiadają `{ error: 'kod', message: 'Zdanie po polsku.' }`
 * albo `{ error: 'Zdanie po polsku.' }`. Do 01.10.2026 człowiek dostawał całą
 * kopertę jako tekst (`{"error":"Nie udało się…"}`) — zdanie było, tylko
 * w nawiasach klamrowych.
 *
 * Gdy `error` jest ZDANIEM, a `message` szczegółem (`{ error: 'Genesis start
 * failed', message: err.message }`), idą oba — sam szczegół techniczny bez
 * kontekstu mówiłby mniej niż dawna surowa koperta. Kod (`model_spoza_rejestru`)
 * nie jest zdaniem i zostaje pominięty.
 */
const KOD_BLEDU = /^[a-z0-9_.-]+$/;

function zdanieZOdpowiedzi(bodyText: string): string {
  const tekst = bodyText?.trim() ?? '';
  if (!tekst.startsWith('{')) return tekst;
  try {
    const koperta = JSON.parse(tekst) as Record<string, unknown>;
    const pole = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    const message = pole(koperta.message);
    const error = pole(koperta.error);
    if (message && error && error !== message && !KOD_BLEDU.test(error)) {
      return `${error}: ${message}`;
    }
    const zdanie = message ?? error;
    if (zdanie) return zdanie;
  } catch {
    /* nie JSON — zostaje surowy tekst */
  }
  return tekst;
}

/** Map an HTTP status + server text to a friendly PL message. PURE. */
export function nexusErrorMessage(status: number, bodyText: string): string {
  const detail = zdanieZOdpowiedzi(bodyText).slice(0, 300);
  if (status === 401) return 'Sesja wygasła — zaloguj się ponownie.';
  if (status === 403) return detail || 'Brak uprawnień (guardrail / RLS odrzucił operację).';
  if (status === 404) return 'Zasób nie istnieje (404).';
  if (status >= 500) return detail || `Błąd serwera (${status}).`;
  return detail || `Żądanie nie powiodło się (${status}).`;
}
